import React from 'react';
import { motion } from 'framer-motion';
import { BrainCircuit, ArrowRight } from 'lucide-react';

const Slide4Differentiation = () => {
    return (
        <div className="w-full h-full bg-background flex items-center justify-center relative overflow-hidden">
            {/* Background Grid Texture */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{ backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}
            />
            {/* Radial Background */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/10 via-background to-background" />

            <div className="max-w-6xl w-full px-8 relative z-10 flex flex-col items-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl lg:text-6xl font-bold mb-4">From Passive Tracking to <span className="text-primary text-glow">Active Autonomy</span></h2>
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                        ZenCap is an Autonomous Financial Assistant (AFA).
                    </p>
                </motion.div>

                {/* Architecture Visual */}
                <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16 w-full justify-center">

                    {/* Inputs: What it knows */}
                    <motion.div
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-col gap-6 text-center lg:text-right"
                    >
                        <p className="text-primary font-mono text-s uppercase tracking-widest mb-2">1. What it knows</p>
                        {[
                            { label: 'How you spend', sub: 'Using Expense Tracking', sub2: 'Bills, Groceries, Rent' },
                            { label: 'What you want', sub: 'Using Goal Setting', sub2: 'Travel, Home, Savings' },
                            { label: 'How you feel', sub: 'Using Risk Assessment', sub2: 'Your comfort with risk' }
                        ].map((item, i) => (
                            <div key={i} className="px-6 py-4 bg-card border border-border rounded-xl flex flex-col gap-1.5">
                                <h4 className="font-bold text-white text-base">{item.label}</h4>
                                <p className="text-xs text-primary font-medium">{item.sub}</p>
                                <p className="text-xs text-muted-foreground">{item.sub2}</p>
                            </div>
                        ))}
                    </motion.div>

                    <div className="hidden lg:block">
                        <ArrowRight className="w-6 h-6 text-primary/30" />
                    </div>

                    {/* The Engine: How it thinks */}
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="relative w-72 h-72 flex items-center justify-center flex-shrink-0"
                    >
                        {/* Rings */}
                        <div className="absolute inset-0 border-2 border-primary/30 rounded-full animate-spin-slow" style={{ borderStyle: 'dashed' }} />
                        <div className="absolute inset-4 border border-primary/20 rounded-full animate-reverse-spin" />

                        {/* Core */}
                        <div className="w-40 h-40 bg-primary/10 rounded-full flex flex-col items-center justify-center backdrop-blur-sm border border-primary/50 shadow-[0_0_60px_rgba(0,255,0,0.2)]">
                            <BrainCircuit className="w-12 h-12 text-primary mb-2" />
                            <span className="text-xs font-bold text-primary tracking-[0.2em]">ADI ENGINE</span>
                            <span className="text-[10px] text-primary/60 mt-1 uppercase">Thinking...</span>
                        </div>

                        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 whitespace-nowrap">
                            <p className="text-primary font-mono text-xl uppercase tracking-widest text-center">2. How it thinks</p>
                            <p className="text-s text-muted-foreground text-center mt-1 italic">Real-time Analysis & Reasoning</p>
                        </div>
                    </motion.div>

                    <div className="hidden lg:block">
                        <ArrowRight className="w-6 h-6 text-primary/30" />
                    </div>

                    {/* Output: What it does */}
                    <motion.div
                        initial={{ x: 20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className="flex flex-col items-center lg:items-start"
                    >
                        <p className="text-primary font-mono text-s uppercase tracking-widest mb-2">3. What it does</p>
                        <div className="px-8 py-8 bg-card border-2 border-primary rounded-2xl shadow-[0_0_40px_rgba(0,255,0,0.15)] relative group hover:scale-105 transition-transform">
                            <div className="absolute -top-3 -right-3 bg-primary text-black text-[10px] font-bold px-2 py-1 rounded-full uppercase">Output</div>
                            <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">Smart Money Moves</h3>
                            <p className="text-sm text-primary font-medium mb-4">"Always asks for your Permission"</p>
                            <ul className="space-y-2">
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <div className="w-1 h-1 bg-primary rounded-full" />
                                    Personalized Nudges
                                </li>
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <div className="w-1 h-1 bg-primary rounded-full" />
                                    Risk Management
                                </li>
                                <li className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <div className="w-1 h-1 bg-primary rounded-full" />
                                    Goal Fulfillment
                                </li>
                            </ul>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default Slide4Differentiation;
